/**
 * tableHelpers — shared sort + column-filter logic for the External module.
 *
 * Promoted from ExternalExplorer's displayRows() (the module's best-in-class
 * typed + discrete filter implementation) so EVERY table — DataTable-based or
 * bespoke — reaches feature parity from ONE implementation instead of the ~9
 * hand-rolled toggleSort copies and scattered filter code.
 *
 * A column descriptor (the shape DataTable and helpers agree on):
 *   { field, header, type, format, align, width, sticky, sortable, filterable, cell }
 *     type ∈ 'text' | 'number' | 'currency' | 'percent' | 'per_1k' | 'date' | 'badge'
 *     format: optional (value, row) => string  (display override)
 *
 * NUMERIC types (number/currency/percent/per_1k) sort + filter numerically.
 * 'date' sorts chronologically (local-parsed — see parseLocalDate).
 * everything else is text (contains + discrete pick-list).
 */

const NUMERIC_TYPES = new Set(['number', 'currency', 'percent', 'per_1k'])

export function isNumericType(type) {
  return NUMERIC_TYPES.has(type)
}

/**
 * Parse a value to a comparable key for sorting.
 * Date-only strings are parsed as LOCAL dates (never UTC) to avoid the
 * timezone off-by-one that bit the TOC view.
 */
function parseLocalDate(val) {
  if (val == null || val === '') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(val))
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val)
  return isNaN(d) ? null : d.getTime()
}

/**
 * Comparator factory. Returns (a, b) => number for the given column + direction.
 *   dir: 'asc' | 'desc'
 */
export function makeComparator(col, dir) {
  const sign = dir === 'desc' ? -1 : 1
  const field = col.field
  if (isNumericType(col.type)) {
    return (a, b) => {
      let av = Number(a[field]); let bv = Number(b[field])
      if (Number.isNaN(av)) av = -Infinity
      if (Number.isNaN(bv)) bv = -Infinity
      return (av - bv) * sign
    }
  }
  if (col.type === 'date') {
    return (a, b) => {
      const av = parseLocalDate(a[field]) ?? -Infinity
      const bv = parseLocalDate(b[field]) ?? -Infinity
      return (av - bv) * sign
    }
  }
  // text
  return (a, b) => {
    const av = String(a[field] == null ? '' : a[field]).toLowerCase()
    const bv = String(b[field] == null ? '' : b[field]).toLowerCase()
    if (av < bv) return -1 * sign
    if (av > bv) return 1 * sign
    return 0
  }
}

/**
 * Sort rows by a sort model { key, dir } against a column list. Returns a NEW
 * array; a null/empty key returns rows unchanged (stable original order).
 */
export function sortRows(rows, sortModel, columns) {
  if (!sortModel || !sortModel.key) return rows
  const col = columns.find(c => c.field === sortModel.key)
  if (!col) return rows
  return rows.slice().sort(makeComparator(col, sortModel.dir))
}

/**
 * Is a single column filter "active" (worth applying)?
 * filter shapes:
 *   numeric: { op: 'gte'|'lte'|'eq'|'between', value, min, max }
 *   text:    { text, selected: [] }   (contains + discrete pick-list)
 */
export function isFilterActive(col, f) {
  if (!f) return false
  if (isNumericType(col.type)) {
    if (f.op === 'between') {
      return (f.min !== '' && f.min != null) || (f.max !== '' && f.max != null)
    }
    return !!f.op && f.value !== '' && f.value != null
  }
  return (f.text != null && f.text !== '') || (Array.isArray(f.selected) && f.selected.length > 0)
}

/** Does one row pass one column's filter? */
function rowMatchesColumn(row, col, f) {
  const raw = row[col.field]
  if (isNumericType(col.type)) {
    const n = Number(raw)
    if (Number.isNaN(n)) return false
    if (f.op === 'between') {
      const lo = f.min === '' || f.min == null ? -Infinity : Number(f.min)
      const hi = f.max === '' || f.max == null ? Infinity : Number(f.max)
      return n >= lo && n <= hi
    }
    const val = Number(f.value)
    if (f.op === 'gte') return n >= val
    if (f.op === 'lte') return n <= val
    if (f.op === 'eq') return n === val
    return true
  }
  const sval = String(raw == null ? '' : raw)
  if (Array.isArray(f.selected) && f.selected.length && !f.selected.includes(sval)) return false
  if (f.text != null && f.text !== '' && !sval.toLowerCase().includes(String(f.text).toLowerCase())) return false
  return true
}

/**
 * Apply a column-filter map { field: filter } to rows against a column list.
 * Returns a NEW filtered array (or the same array if nothing active).
 */
export function filterRows(rows, filterMap, columns) {
  if (!filterMap) return rows
  const active = columns.filter(c => c.filterable && isFilterActive(c, filterMap[c.field]))
  if (!active.length) return rows
  return rows.filter(row => active.every(c => rowMatchesColumn(row, c, filterMap[c.field])))
}

/** Convenience: filter THEN sort (the order the tables want). */
export function applyView(rows, { filterMap, sortModel } = {}, columns) {
  return sortRows(filterRows(rows, filterMap, columns), sortModel, columns)
}
